
import Vue, { VNodeData } from 'vue';
import { Component } from 'nuxt-property-decorator';
import { MetaInfo } from 'vue-meta';
import { MyHotelLocalNavi } from '../-components';
import HotelViewRoot from '../../-index';
import {
  HActivityList,
  HSimpleCoverTitle,
  HBannerLink,
  toBannerLinkProps,
  HIcon,
} from '~/components';
import { ActivityCommons, ActivitiesRequestResult } from '~/schemes';
import { HActivitiesFilterProvider } from '~/components/HActivitiesFilter';

/**
 * フィルター機能を使うアクティビティー件数の条件
 */
const USE_FILTER_COUNT = 5;

@Component<MyView>({
  inject: ['hotelViewRoot'],
  async asyncData({ $commons, $hotel, params, error, redirect, $error }) {
    try {
      const { hotel_slug } = params;
      const hotelSource = await $hotel.getBySlug(hotel_slug);
      if (!hotelSource) {
        return error({
          statusCode: 404,
        });
      }

      /**
       * アクティビティー基本設定がなければ施設TOPにリダイレクトしてあげる
       */
      if (!hotelSource.activitySettings) {
        const location = $hotel.location(undefined, hotelSource);
        return redirect(location);
      }

      const [activityCommons, activitiesWithKeywords] = await Promise.all([
        $commons.getActivity(),
        $hotel.activitiesWithKeywordsBySlug(hotel_slug),
      ]);

      return {
        activityCommons,
        activitiesWithKeywords,
      };
    } catch (_err) {
      $error.throw(_err);
    }
  },
  head() {
    const { hotel, activitySettings: settings } = this;
    const { description } = settings;
    const title = this.$t('label.activities') as string;
    const head: MetaInfo = {
      title,
      meta: [
        { hid: 'og:title', property: 'og:title', content: title },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `${this.$navigation.origin}/${this.$language.current}/hotels/${hotel.slug}/activities/`,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: this.$res.img(settings.ogImage),
        },
      ],
    };

    if (description) {
      head.meta!.push(
        {
          hid: 'description',
          name: 'description',
          content: description,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
      );
    }
    return head;
  },
  render() {
    const {
      useFilter,
      title,
      titleDeco,
      mainLead,
      mainVisual,
      notification,
      hotel,
      bannerLinks,
    } = this;

    return (
      <div
        staticClass="my-hotel-activities-index-view"
        class={{ 'my-hotel-activities-index-view--use-filter': useFilter }}>
        <MyHotelLocalNavi />
        <HActivitiesFilterProvider
          staticClass="my-provider"
          activityCommons={this.activityCommons}
          matchedKeywords={this.activitiesWithKeywords.matchedKeywords}
          activities={this.activitiesWithKeywords.activities}
          autoScrollPosition={this.getAutoScrollPosition}
          scopedSlots={{
            default: (provider) => (
              <div staticClass="my-inner">
                <HSimpleCoverTitle
                  staticClass="my-inner__title"
                  title={title}
                  deco={titleDeco}
                  subTitle={mainLead}
                  image={mainVisual}
                />

                {!!notification && (
                  <div
                    v-wysiwyg={notification}
                    staticClass="my-notification h-container h-container--min"
                  />
                )}
                {!!bannerLinks.length && (
                  <div
                    class={[
                      'my-hotel-activities__bannerLinks-wrapper',
                      {
                        'my-hotel-activities__bannerLinks-wrapper--is-filter': useFilter,
                      },
                    ]}>
                    <div
                      class={[
                        'my-hotel-activities__bannerLinks',
                        `my-hotel-activities__bannerLinks--${this.bannerLinks.length}`,
                      ]}>
                      {bannerLinks.map(({ banner, media }) => (
                        <HBannerLink
                          key={banner.key}
                          props={banner}
                          src={media && media.md}
                          staticClass="my-hotel-activities__bannerLinks__item"
                          style={
                            media
                              ? {
                                  '--image-lg': `url(${media.lg})`,
                                  '--image-md': `url(${media.md})`,
                                  '--image-sm': `url(${media.sm})`,
                                }
                              : { '--banner-image': 'gray' }
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}
                {useFilter && (
                  <div staticClass="my-filter-wrapper">
                    <div staticClass="my-filter-wrapper__inner">
                      {provider.renderForm(this.baseFormData)}
                    </div>
                  </div>
                )}

                <div staticClass="my-contents">
                  <HActivityList
                    staticClass="my-list"
                    commons={this.activityCommons}
                    items={provider.filteredActivities}
                    hotel={hotel}
                  />
                </div>

                {this.drawerFormIsOpened && (
                  <div
                    key="drawer-form-backdrop"
                    staticClass="my-drawer-form-backdrop"
                    onClick={this.toggleDrawerForm}
                  />
                )}

                <div
                  staticClass="my-drawer-form"
                  class={{
                    'my-drawer-form--opened': this.drawerFormIsOpened,
                    'my-drawer-form--active': this.drawerFormIsActive,
                  }}>
                  <div staticClass="my-drawer-form__body">
                    {provider.renderForm({
                      staticClass: 'my-drawer-form__filters',
                      props: {
                        useAutoScroll: true,
                      },
                    })}
                  </div>
                  <div staticClass="my-drawer-form__activator-wrapper">
                    <button
                      type="button"
                      staticClass="my-drawer-form__activator"
                      onClick={this.toggleDrawerForm}>
                      &emsp;
                      {this.$t(
                        this.drawerFormIsOpened
                          ? 'action.close'
                          : 'action.filters',
                      )}
                      <HIcon
                        staticClass="my-drawer-form__activator__icon"
                        name="keyboard-arrow-down"
                      />
                    </button>
                  </div>
                </div>
              </div>
            ),
          }}
        />
      </div>
    );
  },
  watch: {
    // ブレイクポイントがPCに切り替わったらドロワーフォームを閉じておく
    '$mq.match.wide'(isWide: boolean) {
      isWide && this.closeDrawerForm();
    },
    // ドロワーフォームが有効でなくなる時は閉じておく
    drawerFormIsActive(drawerFormIsActive: boolean) {
      !drawerFormIsActive && this.closeDrawerForm();
    },
  },
})
export default class MyView extends Vue {
  readonly hotelViewRoot!: HotelViewRoot;

  /** アクティビティ共通設定 */
  readonly activityCommons: ActivityCommons = null as any;

  /** アクティティリストと検索表示用キーワードリスト */
  readonly activitiesWithKeywords!: ActivitiesRequestResult;

  /**
   * フォームが画面内に入っているか
   *
   * * `null` の場合はまだ検知していない時
   */
  formIsInviewed: boolean | null = null;

  /**
   * ドロワーフォームが開かれているか
   */
  drawerFormIsOpened = false;

  /**
   * ドロワーフォームがアクティブ（引き出しか全体を画面内に出す）か
   */
  get drawerFormIsActive(): boolean {
    return this.formIsInviewed === false;
  }

  /**
   * ベースのフィルタフォームのVNodeデータ
   */
  get baseFormData(): VNodeData {
    return {
      directives: [
        {
          name: 'inview',
          value: {
            in: () => {
              this.formIsInviewed = true;
            },
            out: () => {
              this.formIsInviewed = false;
            },
          },
        },
      ],
    };
  }

  /**
   * タイトルのデコレーション
   *
   * * リゾナーレの時だけ、欧文テキストを記載する
   *
   * @see https://hoshinoresorts-dev.slack.com/archives/C01PKFF4753/p1668563143395969
   */
  get titleDeco() {
    if (!this.$theme.is('risonare') || this.$language.current === 'en') {
      return;
    }
    return 'Activities';
  }

  get title() {
    const { sections } = this.$theme.current;
    const activitiesSection = sections.find(({ key }) => key === 'activities');
    if (activitiesSection) {
      return activitiesSection.label;
    }
    return this.$t('label.activity') as string;
  }

  get useFilter() {
    return this.activitiesWithKeywords.activities.length > USE_FILTER_COUNT;
  }

  get hotel() {
    return this.hotelViewRoot.hotel;
  }

  get activitySettings() {
    const { activitySettings } = this.hotel;
    if (!activitySettings) {
      throw new Error('missing activitySettings');
    }
    return activitySettings;
  }

  get mainVisual() {
    return this.activitySettings.mainVisual;
  }

  get mainLead() {
    const { mainVisual } = this;
    if (!mainVisual) return;
    return this.activitySettings.lead;
  }

  get notification() {
    return this.activitySettings.notification;
  }

  get bannerLinks() {
    return this.activitySettings.bannerLinks.map((banner) => {
      const lg = toBannerLinkProps(banner, 'lg').src;
      const md = toBannerLinkProps(banner, 'md').src;
      const sm = toBannerLinkProps(banner, 'sm').src;

      return {
        banner,
        media:
          lg || md || sm
            ? {
                lg,
                md,
                sm,
              }
            : null,
      };
    });
  }

  /**
   * ドロワーフォームを閉じる
   */
  closeDrawerForm() {
    this.drawerFormIsOpened = false;
  }

  /**
   * ドロワーフォームの開閉状態をトグルする
   */
  toggleDrawerForm() {
    this.drawerFormIsOpened = !this.drawerFormIsOpened;
  }

  /**
   * リストフィルタ結果変更検知時に自動スクロールさせる先の要素を取得するための関数
   */
  getAutoScrollPosition(): Element | null | undefined {
    return this.$el.querySelector('.my-filter-wrapper');
  }
}
